import * as React from 'react';
import { getAuth } from 'firebase/auth';
import { app, API_BASE_URL } from '../../firebase/firebaseConfig';
import {
  HCard,
  HPrimaryButton,
  HSecondaryButton,
  SectionLabel,
} from '../h';

/* BillingSection — subscription info on the Account tab.

   Rendered by AccountTab ONLY when the admin has enabled `billingInfoVisible`
   on the account (and the server re-checks the flag on every endpoint).

   Shows: plan name (Warm/Glow), headline price, lifetime discount, the actual
   monthly amount, next payment date, and — as the only "issue" we report — a
   failed payment. Nothing is said about the customer's card: we cannot see it
   reliably, and Stripe already tells them. Buttons, gated differently on
   purpose:
     - "Manage billing & invoices" → Stripe customer portal (card + invoices).
       Shown whenever the account has a Stripe customer to open a portal for —
       INCLUDING before a subscription is set up. This is the only route a
       customer has to replace a card, and an expired card is exactly what
       needs fixing at that point, so gating it on having a plan would leave
       them stuck.
     - "Cancel subscription" → confirmation overlay → cancellation REQUEST
       (a human follows up; nothing is cancelled automatically). Stays gated on
       there being a plan: with no subscription there is nothing to cancel.
*/

// A Canadian customer only ever sees their own (cad) prices, so plain '$'.
const CURRENCY_SYMBOL = { gbp: '£', usd: '$', eur: '€', cad: '$' };

function fmtMoney(minor, currency) {
  if (minor === null || minor === undefined || isNaN(minor)) return '—';
  const sym = CURRENCY_SYMBOL[(currency || '').toLowerCase()] || '';
  const amount = (minor / 100).toFixed(2);
  return sym ? `${sym}${amount}` : `${amount} ${(currency || '').toUpperCase()}`;
}

function fmtDate(ms) {
  if (!ms) return null;
  try {
    return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return null;
  }
}

// Only one issue is reported, and only because it is read from the live
// subscription rather than from anything we store. We deliberately say NOTHING
// about the customer's card — see the note in billing/userBilling.js.
const ISSUE_TEXT = {
  payment_problem: 'Your last payment didn’t go through. Please check your card via “Manage billing & invoices”.',
};

// Same facts, for the case where we have no Stripe customer to open a portal
// for and the button below is therefore not rendered. INVARIANT: no message
// naming “Manage billing & invoices” may appear while that button is hidden —
// pointing a customer at a control that isn't on the page is the same dead end
// this section exists to remove.
const ISSUE_TEXT_NO_PORTAL = {
  payment_problem: 'Your last payment didn’t go through. Please contact us at hq@higlo.co and we’ll sort it out.',
};

function InfoRow({ label, children }) {
  return (
    <div className="flex justify-between gap-3 py-1">
      <span className="text-[13px] text-slate-500 flex-shrink-0">{label}</span>
      <span className="text-[13px] text-slate-900 font-medium text-right">{children}</span>
    </div>
  );
}

export function BillingSection({ accountUUID }) {
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [portalBusy, setPortalBusy] = React.useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = React.useState(false);
  const [cancelBusy, setCancelBusy] = React.useState(false);
  const [cancelDone, setCancelDone] = React.useState(false);
  const [portalError, setPortalError] = React.useState(null);
  const [cancelError, setCancelError] = React.useState(null);

  const callApi = React.useCallback(async (path, body) => {
    const auth = getAuth(app);
    const idToken = await auth.currentUser.getIdToken();
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify(body || {}),
    });
    const data = await response.json();
    if (!response.ok || data.success === false) {
      throw new Error(data.error || `Request failed (${response.status})`);
    }
    return data;
  }, []);

  const loadSummary = React.useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const data = await callApi('/user/billingSummary');
      setSummary(data.summary || null);
    } catch (err) {
      console.warn('billingSummary failed', err.message);
      setSummary(null);
      setLoadFailed(true);
    }
    setLoading(false);
  }, [callApi]);

  React.useEffect(() => { loadSummary(); }, [loadSummary, accountUUID]);

  // Opening the Stripe portal navigates away in the SAME tab; when the user
  // comes back via the browser back button the page is often revived from the
  // back/forward cache with the pre-navigation state — i.e. the button stuck
  // on "Opening…" and disabled. Reset the in-flight portal state on restore.
  React.useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) {
        setPortalBusy(false);
        setPortalError(null);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const handleOpenPortal = async () => {
    if (portalBusy) return;
    setPortalBusy(true);
    setPortalError(null);
    try {
      const data = await callApi('/user/billingPortalSession');
      if (data.url) {
        window.location.href = data.url;
        return; // navigating away
      }
      throw new Error('No portal URL returned');
    } catch (err) {
      console.warn('billingPortalSession failed', err.message);
      setPortalError('Sorry — we couldn’t open the billing page. Please try again in a moment.');
    }
    setPortalBusy(false);
  };

  const handleConfirmCancel = async () => {
    if (cancelBusy) return;
    setCancelBusy(true);
    setCancelError(null);
    try {
      await callApi('/user/requestSubscriptionCancel');
      setCancelDone(true);
      setShowCancelConfirm(false);
    } catch (err) {
      console.warn('requestSubscriptionCancel failed', err.message);
      setCancelError('Sorry — your request didn’t go through. Please try again, or contact us at hq@higlo.co.');
    }
    setCancelBusy(false);
  };

  if (loading) return null;

  // AccountTab only renders this section when billing is enabled on the
  // account, so a load failure deserves an honest retry state rather than
  // silently pretending billing doesn't exist.
  if (loadFailed) {
    return (
      <div className="mb-4">
        <SectionLabel className="mb-2">Subscription</SectionLabel>
        <HCard className="p-3">
          <div className="text-[13px] text-slate-500 mb-2">
            We couldn't load your subscription details.
          </div>
          <HSecondaryButton type="button" onClick={loadSummary} className="w-full text-[13px]">
            Try again
          </HSecondaryButton>
        </HCard>
      </div>
    );
  }

  // Hidden or admin turned it off server-side — show nothing.
  if (!summary || !summary.visible) return null;

  const cancelRequested = cancelDone || summary.cancelRequested;
  const issues = summary.issues || [];
  const nextPaymentDate = fmtDate(summary.nextPaymentMs);
  // The server reports whether a portal can actually be opened for this
  // account. If it's absent the deployed functions predate that field, so fall
  // back to the old rule (plan ⇒ portal) rather than offering a button that
  // would fail — the webapp and the functions deploy separately.
  const portalAvailable = summary.portalAvailable === undefined
    ? !!summary.planName
    : summary.portalAvailable;
  // Show only codes we have copy for. This matters because the card-derived
  // codes were deliberately retired: a webapp deployed ahead of the functions
  // can still be handed `card_expired` and friends, and showing a customer the
  // raw identifier would be worse than showing them nothing. Filtering here
  // rather than at render also keeps the panel from appearing empty.
  const issueText = portalAvailable ? ISSUE_TEXT : ISSUE_TEXT_NO_PORTAL;
  const shownIssues = issues.filter((code) => {
    if (issueText[code]) return true;
    // Silence is right for the customer, but a dropped code should still be
    // diagnosable: during deploy skew it's expected, otherwise it means a code
    // was renamed or added server-side without copy here.
    console.warn(`BillingSection: no copy for issue code "${code}" — not shown`);
    return false;
  });

  return (
    <div className="mb-4">
      <SectionLabel className="mb-2">Subscription</SectionLabel>
      <HCard className="p-3">
        {summary.planName ? (
          <>
            <InfoRow label="Plan">{summary.planName}</InfoRow>
            {summary.discountPercent > 0 ? (
              <>
                <InfoRow label="Standard price">{fmtMoney(summary.headlineMinor, summary.currency)}/month</InfoRow>
                <InfoRow label="Your discount">{summary.discountPercent}%</InfoRow>
                <InfoRow label="You pay">{fmtMoney(summary.monthlyMinor, summary.currency)}/month</InfoRow>
              </>
            ) : (
              // No discount — a standard-price/discount/you-pay breakdown would
              // just repeat the same figure; show a single price row.
              <InfoRow label="Price">
                {fmtMoney(summary.monthlyMinor != null ? summary.monthlyMinor : summary.headlineMinor, summary.currency)}/month
              </InfoRow>
            )}
            {nextPaymentDate && (
              <InfoRow label={summary.state === 'scheduled' ? 'First payment' : 'Next payment'}>
                {nextPaymentDate}
              </InfoRow>
            )}
          </>
        ) : (
          <div className="text-[13px] text-slate-500 py-1">
            Your subscription is being set up — details will appear here soon.
          </div>
        )}

        {shownIssues.length > 0 && (
          <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 p-2">
            {shownIssues.map((code) => (
              <div key={code} className="text-[12px] text-amber-800 py-0.5">
                {issueText[code]}
              </div>
            ))}
          </div>
        )}

        {/* The portal is the customer's only way to replace a card, so it is
            offered as soon as there is a Stripe customer to open it for. */}
        {portalAvailable && (
          <div className="mt-3 space-y-2">
            <HPrimaryButton
              type="button"
              onClick={handleOpenPortal}
              disabled={portalBusy}
              className="w-full text-[13px]"
            >
              {portalBusy ? 'Opening…' : 'Manage billing & invoices'}
            </HPrimaryButton>
            {portalError && (
              <div className="text-[12px] text-red-600 text-center">{portalError}</div>
            )}
          </div>
        )}

        {/* Cancellation needs an actual subscription to cancel. */}
        {summary.planName && (
          <div className="mt-2 space-y-2">
            {cancelRequested ? (
              <div className="text-[12px] text-slate-500 text-center pt-1">
                Cancellation requested — a member of our team will be in touch to arrange the
                return of your Hearths and complete your cancellation.
              </div>
            ) : (
              <HSecondaryButton
                type="button"
                onClick={() => setShowCancelConfirm(true)}
                className="w-full text-[13px] text-slate-500 hover:text-slate-700"
              >
                Cancel subscription
              </HSecondaryButton>
            )}
          </div>
        )}
      </HCard>

      {showCancelConfirm && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => !cancelBusy && setShowCancelConfirm(false)}
        >
          <HCard className="p-4 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="text-[15px] font-semibold text-slate-900 mb-2">
              We're sad to see you go
            </div>
            <div className="text-[13px] text-slate-600 leading-snug mb-4">
              We understand you no longer want to use Higlo. If you confirm, a member of our
              team will be in touch to arrange the return of your Hearths and to complete the
              cancellation of your subscription.
            </div>
            {cancelError && (
              <div className="text-[12px] text-red-600 mb-3">{cancelError}</div>
            )}
            <div className="space-y-2">
              <HPrimaryButton
                type="button"
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelBusy}
                className="w-full text-[13px]"
              >
                Keep my subscription
              </HPrimaryButton>
              <HSecondaryButton
                type="button"
                onClick={handleConfirmCancel}
                disabled={cancelBusy}
                className="w-full text-[13px] text-red-500 hover:text-red-600"
              >
                {cancelBusy ? 'Sending…' : 'Confirm cancellation'}
              </HSecondaryButton>
            </div>
          </HCard>
        </div>
      )}
    </div>
  );
}

export default BillingSection;
